import { checkFirstAndLastName } from '@shef/validators/dist/src/checkName';
import { ClientEvents } from 'common/events/ClientEvents';
import { isLeft } from 'fp-ts/lib/Either';
import React, { useEffect, useState } from 'react';
import { CurrentUserFieldsFragment, OAuthLoginType } from 'src/gqlReactTypings.generated.d';
import { useFormattedPhoneInput } from 'src/shared/hooks/useFormattedPhoneInput';
import { useTextInputCallback } from 'src/shared/hooks/useInputCallback';
import { isValidPhone } from 'src/shared/utils/PhoneUtils';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { useTracker } from '../../shared/hooks/useTracker';
import { OAUTH_LOGIN_TYPE_TO_AUTH_METHOD_RECORD, useLoginOrSignUpWithOAuth } from '../oAuth/useLoginOrSignUpWithOAuth';
import { useGateType } from './GateTypeContext';
import { OAuthConsent, OAuthErrorMessage, OAuthFieldset, OAuthNameFields, OAuthTextField } from './oAuthFormFields';

interface OAuthAdditionalFieldsFormProps {
  firstName?: string | null;
  lastName?: string | null;
  token: string;
  type: OAuthLoginType;
  onSuccess: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void;
  signUpEntryPoint?: string | null;
}

export const OAuthAdditionalFieldsForm: React.FC<OAuthAdditionalFieldsFormProps> = ({
  firstName: initialFirstName,
  lastName: initialLastName,
  onSuccess,
  token,
  type,
  signUpEntryPoint,
}) => {
  const [nameErrorMessage, setNameErrorMessage] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [firstName, onFirstNameChange] = useTextInputCallback(initialFirstName ?? '');
  const [lastName, onLastNameChange] = useTextInputCallback(initialLastName ?? '');
  const [phoneErrorMessage, setPhoneErrorMessage] = useState<string | undefined>();
  const tracker = useTracker();
  const gateType = useGateType();
  const onError = () => {
    setErrorMessage('Login failed. Please try again, or try using a different method.');
  };

  const { login } = useLoginOrSignUpWithOAuth({
    type,
    onError,
    onSuccess,
    requireZipCode: false,
    signUpEntryPoint,
  });

  const [displayPhone, onPhoneChange, rawPhone] = useFormattedPhoneInput();
  const [tosChecked, setTosChecked] = useState(false);

  useEffect(() => {
    tracker.track(ClientEvents.LOGIN_MODAL_SIGNUP_PAGE, {
      signUpEntryPoint,
      method: 'google',
      gateType,
    });
  }, [tracker, signUpEntryPoint, gateType]);

  const validateForm = () => {
    setErrorMessage(undefined);
    setNameErrorMessage(undefined);
    setPhoneErrorMessage(undefined);

    const nameCheck = checkFirstAndLastName({ firstName, lastName });
    if (isLeft(nameCheck)) {
      setNameErrorMessage(nameCheck.left);
    }

    const isPhoneValid = isValidPhone(rawPhone);
    if (!isPhoneValid) {
      setPhoneErrorMessage('Please enter a valid phone number');
    }

    if (!tosChecked) {
      setErrorMessage('You must accept our Terms of Service to continue');
    }

    return isPhoneValid && tosChecked;
  };

  const handleSubmitForm = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const isFormValidated = validateForm();
    if (isFormValidated) {
      try {
        // additional fields form is only for google, apple uses missing fields form
        tracker.track(ClientEvents.LOGIN_MODAL_SIGNUP_SUBMIT, {
          method: 'google',
        });
        await login(token, {
          firstName,
          lastName,
          phone: rawPhone,
          hasAcceptedTermsOfService: tosChecked,
        });
        // REGISTER_SUCCESS does not fire in useHandleLogin for SSO, so we need it here
        tracker.track(ClientEvents.REGISTER_SUCCESS, {
          method: OAUTH_LOGIN_TYPE_TO_AUTH_METHOD_RECORD[type],
          promoCode: undefined,
        });
      } catch (error) {
        onError();
      }
    }
  };

  // Handle Enter key for form submission
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitForm();
    }
  };

  return (
    <form onSubmit={handleSubmitForm} onKeyDown={handleKeyDown} data-cy='additional-fields-form'>
      <OAuthFieldset>
        <OAuthNameFields
          firstName={firstName}
          lastName={lastName}
          onFirstNameChange={onFirstNameChange}
          onLastNameChange={onLastNameChange}
          errorMessage={nameErrorMessage}
          dataCyPrefix='additional-fields'
        />
        <OAuthTextField
          label='Phone Number'
          name='phone'
          value={displayPhone}
          onChange={onPhoneChange}
          errorMessage={phoneErrorMessage}
          dataCy='additional-fields-phone'
        />
        <OAuthConsent
          checked={tosChecked}
          onChange={setTosChecked}
          dataCy='additional-fields-tos'
          cta='Continue'
          legacyLinkClassName='primary font-bold !text-eggplant-400 underline'
          error={errorMessage ? <OAuthErrorMessage>{errorMessage}</OAuthErrorMessage> : null}
        />
        <Button buttonType={ButtonType.Primary} iconName='arrowRight' renderFullWidth disabled={!tosChecked}>
          Continue
        </Button>
      </OAuthFieldset>
    </form>
  );
};
